import { useState, useEffect, useMemo, useCallback } from "react";
import { AppProps } from "next/app";
import { useRouter } from "next/router";
import Head from "next/head";
import dynamic from "next/dynamic";
import { ConfigProvider, Layout } from "antd";
import { RecoilRoot } from "recoil";

// Critical styles - load synchronously (above the fold)
import "../src/styles/index.scss";
import "../src/styles/Loader.css";
import "../src/components/Header/index.scss";

// Core page styles
import "../src/page/HomePage/index.scss";
import "../src/page/HomePage/TileContainer/index.scss";
import "../src/page/HomePage/TileContainer/Tile/index.scss";
import "../src/components/Cards/index.scss";

// Secondary styles (still needed but not critical path)
import "../src/page/HomePage/TileContainer/TurfInfoList/index.scss";
import "../src/page/HomePage/TileContainer/TurfPricingDetails/index.scss";
import "../src/page/HomePage/TileContainer/CourtPricingDetails/index.scss";
import "../src/page/HomePage/TileContainer/CourtInfoList/index.scss";
import "../src/page/HomePage/TileContainer/CourtTile/index.scss";
import "../src/components/Cards/CourtMobileCard.scss";
import "../src/page/HomePage/TileContainer/Search/index.scss";
import "../src/page/HomePage/Booking/index.scss";
import "../src/page/HomePage/SingleTileContainer/index.scss";
import "../src/page/NoDataFound/noDataFound.scss";
import "../src/components/Dropdown/index.scss";
import "../src/components/ImagesCarousel/index.scss";
import "../src/components/SelectLocation/location.scss";
import "../src/components/Location/location.scss";
import "../src/components/PrivacyPolicy/index.scss";
import "../src/components/AboutUs/index.scss";
import "../src/components/Disclaimer/index.scss";
import "../src/page/SuccessPage/index.scss";
import "../src/page/SuccessPage/FeedbackPage/index.scss";
import "../src/page/BookingPaymentStatus/index.scss";
import "../src/components/DurationCounter/durationCounter.scss";
import "../src/components/CustomTabs/index.scss";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "../src/components/Footer/index.scss";
import "../src/components/AuthModal/index.scss";
import "../src/styles/my-bookings.scss";
import "../src/components/FilterAndSort/filterSort.module.scss";

// Context providers
import { LocationProvider } from "../src/components/Location/LocationContext";
import { TurfProvider } from "../src/contexts/TurfContext";
import { CitiesProvider } from "../src/contexts/CitiesContext";
import { AuthProvider } from "../src/contexts/AuthContext";
import lightTheme from "../src/theme/lightTheme";
import constants from "../src/utils/constants";
import { getCanonicalCitySlug } from "../src/config/cityData";

// Header is critical for first paint - import normally to avoid hydration issues
import HeaderComponent from "../src/components/Header";
import ErrorBoundary from "../src/components/ErrorBoundary";

// Footer is below the fold - lazy load it (ssr: false avoids hydration mismatch)
const FooterComponent = dynamic(
  () => import("../src/components/Footer"),
  { 
    ssr: false, // Footer doesn't need SSR
    loading: () => null
  }
);

const { Content } = Layout;
const DEFAULT_CITY = "indore";

// Defer non-critical scripts to idle time
const deferToIdle = (callback: () => void) => {
  if (typeof window !== 'undefined') {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(callback, { timeout: 3000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(callback, 2000);
    }
  }
};

// Helper function to get initial city - runs synchronously
function getInitialCity(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_CITY; // SSR fallback
  }
  
  // Priority 1: Get city from URL pathname if on city page
  // Parse from window.location.pathname to avoid router.query timing issues
  const pathname = window.location.pathname;
  if (pathname && pathname !== '/' && pathname !== '/index') {
    const pathParts = pathname.split('/').filter(Boolean);
    const hasCityOnlyRoute = pathParts.length === 1 && pathParts[0] !== 'noData';
    const hasCityAreaRoute = pathParts.length >= 3 && pathParts[1] === 'area';

    if (hasCityOnlyRoute || hasCityAreaRoute) {
      const urlCity = getCanonicalCitySlug(pathParts[0]);
      // Update localStorage to keep in sync
      localStorage.setItem("selectedCity", urlCity);
      return urlCity;
    }
  }
  
  // Priority 2: Get city from localStorage
  const storedCity = localStorage.getItem("selectedCity");
  if (storedCity) {
    return getCanonicalCitySlug(storedCity);
  }
  
  // Priority 3: Default city
  return DEFAULT_CITY;
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const allowedTabs = useMemo(() => new Set(["turfs", "football", "courts", "badminton"]), []);
  // Initialize city from URL or localStorage synchronously to prevent flash
  const [city, setCity] = useState<string>(() => getInitialCity());
  const [turfData, setTurfData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<string>("turfs");

  // Sync city with router when route changes (for city pages)
  useEffect(() => {
    if (!router.isReady) return;
    
    // If on city page (or city-area page), sync city from URL
    if ((router.pathname === '/[city]' || router.pathname === '/[city]/area/[area]') && router.query.city) {
      const urlCity = Array.isArray(router.query.city) 
        ? router.query.city[0] 
        : router.query.city;
      const normalizedUrlCity = urlCity ? getCanonicalCitySlug(urlCity) : null;
      if (normalizedUrlCity && normalizedUrlCity !== 'noData' && normalizedUrlCity !== city) {
        setCity(normalizedUrlCity);
        // Also update localStorage to keep in sync
        localStorage.setItem("selectedCity", normalizedUrlCity);
      }
    }
  }, [router.isReady, router.pathname, router.query.city, city]);

  // Fallback: Get city from localStorage if not set from URL (for non-city pages)
  useEffect(() => {
    if (router.pathname !== '/[city]' && router.pathname !== '/[city]/area/[area]') {
      const storedCity = localStorage.getItem("selectedCity");
      const normalizedStoredCity = storedCity ? getCanonicalCitySlug(storedCity) : null;
      if (normalizedStoredCity && normalizedStoredCity !== city) {
        setCity(normalizedStoredCity);
      }
    }
  }, [router.pathname, city]);

  useEffect(() => {
    if (!router.isReady) return;

    const isCityRoute =
      router.pathname === '/[city]' || router.pathname === '/[city]/area/[area]';

    if (!isCityRoute) {
      if (activeTab !== "turfs") {
        setActiveTab("turfs");
      }
      return;
    }

    const rawTab = Array.isArray(router.query.tab) ? router.query.tab[0] : router.query.tab;
    const nextTab =
      typeof rawTab === "string" && allowedTabs.has(rawTab) ? rawTab : "turfs";

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, allowedTabs, router.isReady, router.pathname, router.query.tab]);

  // Remount pages when the path changes, but not when only the query string
  // does - a `?tab=` switch remounting the whole tree lets any state the page
  // restores on mount fight the state that triggered the switch.
  const pageKey = useMemo(
    () => (router.asPath || "").split("?")[0],
    [router.asPath]
  );

  // Memoised so effects that depend on it (listing pages correcting a stale
  // `?tab=`) run on state changes only, not on every render of the tree.
  const handleTabChange = useCallback((nextTab: string) => {
    if (!allowedTabs.has(nextTab)) return;

    if (!router.isReady) {
      setActiveTab(nextTab);
      return;
    }

    const isCityRoute =
      router.pathname === "/[city]" || router.pathname === "/[city]/area/[area]";

    if (!isCityRoute) {
      setActiveTab(nextTab);
      return;
    }

    const nextQuery = { ...router.query };

    if (nextTab === "turfs") {
      delete nextQuery.tab;
    } else {
      nextQuery.tab = nextTab;
    }

    setActiveTab(nextTab);
    router.push(
      {
        pathname: router.pathname,
        query: nextQuery,
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      }
    );
  }, [allowedTabs, router]);

  // Clear turf data and search when city changes
  useEffect(() => {
    setTurfData([]);
    setSearch("");
  }, [city]);

  // Clear search when navigating to a city page from any other page
  // This ensures search is cleared when going back from a turf detail page
  useEffect(() => {
    if (!router.isReady) return;

    const currentPathname = router.pathname;
    const currentAsPath = router.asPath;

    // Check if current route is a city page
    const isCityPage = currentPathname === '/[city]' || currentPathname === '/[city]/area/[area]';
    
    if (isCityPage) {
      // Check if we just came from a different page by checking sessionStorage
      const previousRoute = sessionStorage.getItem('lastNonCityRoute');
      
      // If we have a record of being on a non-city page before, clear search
      if (previousRoute) {
        // Check if previous route was NOT a city page (detail page, booking, etc.)
        const isPreviousCityPage = previousRoute.match(/^\/[a-z]+$/i) && 
                                   !previousRoute.includes('/area/') &&
                                   !previousRoute.includes('/tournament') &&
                                   !previousRoute.includes('/booking') &&
                                   !previousRoute.includes('/my-bookings');
        
        if (!isPreviousCityPage) {
          // We came from a detail page or other non-city page - clear the search
          setSearch("");
        }
        
        // Clear the sessionStorage after reading
        sessionStorage.removeItem('lastNonCityRoute');
      }
    } else {
      // We're on a non-city page - store this in sessionStorage
      // This helps us detect when user navigates back to city page
      sessionStorage.setItem('lastNonCityRoute', currentAsPath);
    }
  }, [router.isReady, router.pathname, router.asPath]);

  // Initialize Clarity analytics - DEFERRED to after page load (improves LCP)
  useEffect(() => {
    if (process.env.NODE_ENV === "production" && process.env.NEXT_PUBLIC_CLARITY_ID) {
      // Wait for page to be fully loaded before initializing analytics
      const loadClarity = () => {
        import("react-microsoft-clarity").then(({ clarity }) => {
          clarity.init(process.env.NEXT_PUBLIC_CLARITY_ID!);
        });
      };
      
      if (document.readyState === 'complete') {
        setTimeout(loadClarity, 3000); // Delay 3s after load
      } else {
        window.addEventListener('load', () => setTimeout(loadClarity, 3000));
      }
    }
  }, []);

  // Lazy load tournament styles only when needed
  useEffect(() => {
    if (router.pathname.includes('/tournament')) {
      // Dynamically import tournament styles using require to avoid TS module resolution
      // @ts-ignore - Dynamic style imports
      Promise.all([
        import("../src/features/tournament/styles/tournament.scss" as any),
        import("../src/features/tournament/styles/tournament-details.scss" as any),
        import("../src/features/tournament/styles/registration.scss" as any),
        import("../src/features/tournament/styles/registration-success.scss" as any),
        import("../src/features/tournament/styles/prizes.scss" as any),
        import("../src/features/tournament/styles/winners.scss" as any),
        import("../src/features/tournament/styles/payment.scss" as any),
        import("../src/features/tournament/styles/payment-success.scss" as any),
      ]).catch(() => {
        // Styles may already be loaded or don't exist
      });
    }
  }, [router.pathname]);

  const layoutStyle = { minHeight: "100vh" };

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        {/* Default meta tags - will be overridden by page-specific Head tags */}
        {!router.pathname.startsWith('/[city]') && (
          <>
            <meta name="description" content="Book cricket turfs, football grounds & box cricket venues online across India. Easy booking, instant confirmation. Find and reserve sports turfs in 15+ cities on TurfBooking.in" />
            <meta name="keywords" content="turf booking, cricket turf, football turf, box cricket, pickle-ball court , sports ground booking, turf near me, online turf booking" />
          </>
        )}
        <meta property="og:site_name" content="TurfBooking.in" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {/* Preconnect to critical origins */}
        <link rel="preconnect" href="https://s3.ap-south-1.amazonaws.com" />
        <link rel="dns-prefetch" href="https://s3.ap-south-1.amazonaws.com" />
      </Head>
      <RecoilRoot>
        <AuthProvider>
        <LocationProvider>
          <CitiesProvider>
            <TurfProvider>
              <ConfigProvider theme={lightTheme}>
                <Layout style={layoutStyle}>
                  <header>
                  <HeaderComponent
                    name={null}
                    setCity={setCity}
                    setSearch={setSearch}
                    turfData={turfData}
                    activeTab={activeTab}
                    handleTabChange={handleTabChange}
                    city={city}
                  />
                </header>
                <main role="main">
                  <Content>
                    <ErrorBoundary resetKey={pageKey}>
                      <Component
                        {...pageProps}
                        city={city}
                        search={search}
                        setCity={setCity}
                        turfData={turfData}
                        activeTab={activeTab}
                        handleTabChange={handleTabChange}
                        key={pageKey}
                      />
                    </ErrorBoundary>
                  </Content>
                </main>
                <footer>
                  <FooterComponent />
                </footer>
                </Layout>
              </ConfigProvider>
            </TurfProvider>
          </CitiesProvider>
        </LocationProvider>
        </AuthProvider>
      </RecoilRoot>
    </>
  );
}
